import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { APIgetPageFonts, APIsearchGoogleFonts } from '../../../../api/Pages';
import { APIgetFiles } from '../../../../api/Files';
import { DEFAULT_GOOGLE_WEIGHTS, normalizeGoogleFiles } from '../Common/pageFontUtils';

const useResolvedFonts = ({
  fonts,
  pageId,
}) => {
  const [uploadedFontDataByUuid, setUploadedFontDataByUuid] = useState({});
  const [googleFontDataByFamily, setGoogleFontDataByFamily] = useState({});
  const [loading, setLoading] = useState(false);

  const normalizedFonts = useMemo(() => (fonts || []).map(font => ({
    ...font,
    source: font.source || 'uploaded',
    googleFiles: normalizeGoogleFiles(font.googleFiles),
  })), [fonts]);

  const fetchSignature = useMemo(() => JSON.stringify({
    pageId,
    fonts: normalizedFonts.map(font => ({
      source: font.source || 'uploaded',
      uuid: font.uuid || '',
      googleFamily: font.googleFamily || '',
      hasGoogleFiles: Object.keys(font.googleFiles || {}).length > 0,
    })),
  }), [pageId, normalizedFonts]);

  const resolvedFonts = useMemo(() => normalizedFonts.map((font) => {
    if (font.source === 'google') {
      const hydrated = googleFontDataByFamily[font.googleFamily] || {};
      return {
        ...font,
        googleFiles: Object.keys(normalizeGoogleFiles(font.googleFiles)).length > 0
          ? normalizeGoogleFiles(font.googleFiles)
          : normalizeGoogleFiles(hydrated.files),
        googleWeights: (font.googleWeights && font.googleWeights.length > 0)
          ? font.googleWeights
          : (hydrated.weights || DEFAULT_GOOGLE_WEIGHTS),
        url: null,
      };
    }

    const uploadedHydrated = uploadedFontDataByUuid[font.uuid] || {};
    return {
      ...font,
      url: font.url || uploadedHydrated.url || null,
      fileName: uploadedHydrated.fileName || font.fileName || '',
    };
  }), [normalizedFonts, googleFontDataByFamily, uploadedFontDataByUuid]);

  useEffect(() => {
    const parsedSignature = JSON.parse(fetchSignature || '{}');
    const fontsForFetch = parsedSignature.fonts || [];

    if (fontsForFetch.length === 0) {
      setUploadedFontDataByUuid({});
      setGoogleFontDataByFamily({});
      setLoading(false);
      return;
    }

    const fontUuids = fontsForFetch
      .filter(font => font.source !== 'google' && font.uuid)
      .map(font => font.uuid);

    const googleFamiliesToHydrate = [...new Set(
      fontsForFetch
        .filter(font => font.source === 'google' && font.googleFamily && !font.hasGoogleFiles)
        .map(font => font.googleFamily),
    )];

    const missingUploadedUuids = fontUuids.filter((uuid) => !uploadedFontDataByUuid[uuid]);
    const missingGoogleFamilies = googleFamiliesToHydrate
      .filter((family) => !googleFontDataByFamily[family]);

    if ((!pageId || missingUploadedUuids.length === 0) && missingGoogleFamilies.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const mapFilesByUuid = (items = []) => {
      const byUuid = {};
      items.forEach((f) => {
        if (!f?.uuid) {
          return;
        }

        byUuid[f.uuid] = {
          url: f.url || f.urls?.original || f.urls?.preview || null,
          fileName: f.display_name || '',
        };
      });
      return byUuid;
    };

    let pageFontsPromise = Promise.resolve({});
    if (missingUploadedUuids.length > 0 && pageId) {
      pageFontsPromise = APIgetPageFonts(pageId, missingUploadedUuids)
        .then(res => mapFilesByUuid(res.data?.data || []))
        .catch(() => ({}));
    }

    const fallbackFontsPromise = (missingUploadedUuids.length === 0)
      ? Promise.resolve({})
      : APIgetFiles({ uuids: missingUploadedUuids })
        .then((res) => {
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          return mapFilesByUuid(list);
        })
        .catch(() => ({}));

    const googleFilesPromise = (missingGoogleFamilies.length === 0)
      ? Promise.resolve({})
      : Promise.all(
        missingGoogleFamilies.map(family => APIsearchGoogleFonts(family, 30)
          .then((res) => {
            const items = Array.isArray(res.data?.data) ? res.data.data : [];
            const exact = items.find(item => item.family === family);
            return [family, exact || null];
          })
          .catch(() => [family, null])),
      ).then((results) => {
        const map = {};
        results.forEach(([family, fontData]) => {
          map[family] = fontData;
        });
        return map;
      });

    Promise.all([pageFontsPromise, fallbackFontsPromise, googleFilesPromise])
      .then(([uploadedByPageUuid, uploadedFallbackByUuid, googleByFamily]) => {
        if (cancelled) return;

        const uploadedByUuid = {
          ...uploadedFallbackByUuid,
          ...uploadedByPageUuid,
        };

        const hasNewUploaded = Object.keys(uploadedByUuid)
          .some(uuid => !uploadedFontDataByUuid[uuid]);
        const hasNewGoogle = Object.keys(googleByFamily)
          .some(family => !googleFontDataByFamily[family]);

        if (hasNewUploaded) {
          setUploadedFontDataByUuid(prev => ({
            ...prev,
            ...uploadedByUuid,
          }));
        }

        if (hasNewGoogle) {
          setGoogleFontDataByFamily(prev => ({
            ...prev,
            ...googleByFamily,
          }));
        }

        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });

    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [fetchSignature, pageId, uploadedFontDataByUuid, googleFontDataByFamily]);

  return {
    resolvedFonts,
    loading,
  };
};

export default useResolvedFonts;
